import React from 'react';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { RootState } from '@/store';
import { useEventListener } from '@/hooks';
import { TCustomEventName } from '@/constants/custom-event-names.type';

type State = {
  state: 'open' | 'opening' | 'closed' | 'closing';
};

const initialState: State = {
  state: 'closed',
};
export const modalsSlice = createSlice({
  name: 'modalsSlice',
  initialState,
  reducers: {
    setState: (state, action: PayloadAction<State['state']>) => {
      state.state = action.payload;
    },
  },
});

export const selectModalState = (state: RootState) => state.modalsSlice.state;

export const useModalOpen = (delay = 300) => {
  const dispatch = useAppDispatch();

  return () => {
    dispatch(modalsSlice.actions.setState('opening'));
    setTimeout(() => dispatch(modalsSlice.actions.setState('open')), delay);
  };
};

export const useModalClose = (delay = 300) => {
  const dispatch = useAppDispatch();

  return (e?: React.MouseEvent) => {
    e?.preventDefault();

    dispatch(modalsSlice.actions.setState('closing'));
    setTimeout(() => dispatch(modalsSlice.actions.setState('closed')), delay);
  };
};

const MOUNTED_STATES = ['open', 'opening', 'closing'];
export const useModal = (delay = 300) => {
  const modalState = useAppSelector(selectModalState);
  const onOpen = useModalOpen(delay);
  const onClose = useModalClose(delay);
  const [isOpenLocal, setIsOpenLocal] = React.useState(false);

  React.useEffect(() => {
    if (isOpenLocal && modalState === 'closed') {
      setIsOpenLocal(false);
    }
  }, [modalState, isOpenLocal]);

  const handleOpen = React.useCallback(() => {
    setIsOpenLocal(true);
    onOpen();
  }, []);

  const isOpen = isOpenLocal && MOUNTED_STATES.includes(modalState);

  return [isOpen, handleOpen, onClose] as const;
};

export const useEventAwareModal = (
  eventName: TCustomEventName,
  delay = 300,
) => {
  const [isOpenAfterClick, handleOpen] = useModal(delay);
  const isOpenAfterEvent = useOpenModalOnEvent(eventName);

  const isOpen = isOpenAfterClick || isOpenAfterEvent;

  return [isOpen, handleOpen];
};

export const useOpenModalOnEvent = (
  eventName: TCustomEventName,
  delay = 300,
) => {
  const [isOpen, handleOpen] = useModal();
  const [triggered, setTriggered] = React.useState(false);

  const handleTriggered = () => setTriggered(true);

  useEventListener(eventName, () => setTimeout(handleTriggered, delay));

  React.useEffect(() => {
    if (!isOpen && triggered) {
      setTriggered(false);
      handleOpen();
    }
  }, [isOpen, triggered]);

  return isOpen;
};
